import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { validateField, getFieldErrorClass } from "@/utils/fieldHighlighting";
import { Input } from '@/components/cms/ui/input';
import { Textarea } from '@/components/cms/ui/textarea';
import { Card } from '@/components/ui/card';
import { CardTitle, FieldLabel, PageTitle } from '@/components/ui/text';
import { StatusBadge } from '@/components/cms/ui/status-badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/cms/ui/sheet';
import { UIImageUploadDialog } from '@/components/dialogs/ui-image-upload-dialog';
import { SceneCoverImage, ScanImage } from '@/components/cms/ui/specialized-images';
import { ExitConfirmationDialog } from '@/components/dialogs/exitConfirmationDialog';
import { PublishingWarningDialog } from '@/components/dialogs/publishing-warning-dialog';
import { EditorShareDialog } from '@/components/publish-flow/EditorShareDialog';
import { SubscriptionLimitDialog } from '@/components/dialogs/subscription-limit-dialog';
import { DeleteDialog } from '@/components/dialogs/scene-project-delete-dialog';
import { TemplateExitDialog } from '@/components/dialogs/template-exit-dialog';
import { copyScene } from '@/infrastructure/clients/scenesApi';
import { CONSUMED_TEMPLATES_CATEGORY, publishCopyPath } from '@/modules/templateScenes/templateProjects';
import { MissingInfoDialog } from '@/components/dialogs/missing-info-dialog';
import { PublishFlowModal } from '@/components/publish-flow/PublishFlowModal';
import type { PublishDestination, PublishFlowSubmitArgs } from '@/components/publish-flow/types';
import { PublishValidationError } from '@/components/publish-flow/types';
import { PublishIncompleteError, submitStoryForReview, toValidationErrors } from '@/components/publish-flow/reviewSubmissions';
import { makeVersionOption } from '@/components/publish-flow/versionOptions';
import { StoryShareCard } from '@/components/publish-flow/StoryShareCard';
import { writePublishFlowMetadata } from '@/components/publish-flow/writePublishFlowMetadata';
import { StoryVersionHistory } from '@/components/cms/StoryVersionHistory';
import { StoryDestinationsCard } from '@/components/cms/StoryDestinationsCard';
import { useShareLink, clearShareLinkCache, invalidateShareLinks, isPreviewStatus } from '@/hooks/useShareLink';
import { useStoryVisibilitySwitch } from '@/hooks/useStoryVisibilitySwitch';
import { TakedownBanner } from '@/components/editor/TakedownBanner';
import { shareLinkUrl } from '@/modules/databaseService/scenes/shareLinks';
import type { PublishingWarningType } from '@/components/dialogs/publishing-warning-dialog';
import { TemplateDetailsPanel } from '@/components/cms/TemplateDetailsPanel';
import { submitStoryAsTemplate } from '@/modules/templateScenes/templateSubmitService';
import { removeTemplateCopy } from '@/modules/templateScenes/templateCopy';
import { StoryLocationCard } from '@/components/cms/StoryLocationCard';
import { useStoryEditData, type StoryVersion } from '@/hooks/useStoryEditData';
import { pickNewestDraft, pickNewestVersion, pickPublishedVersion } from '@/modules/storyData/versions';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useStoryForm } from './storyEdit/useStoryForm';
import { useStoryChanges } from './storyEdit/useStoryChanges';
import { useStoryImageWrites } from './storyEdit/useStoryImageWrites';
import { useStoryLocationSearch, reverseGeocode } from './storyEdit/useStoryLocationSearch';
import { useTemplateStatus } from './storyEdit/useTemplateStatus';
import { withStoryMetadata, type StoryPublishMetadata } from './storyEdit/storyPublishMetadata';
import LureoButton from '@/common/LureoButton';
import { DesktopOnlyHint } from '@/components/handoff/DesktopOnlyHint';
import { isMobileDevice } from '@/utils/deviceDetection';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { ArrowLeftIcon, ArrowRightIcon, FloppyDiskIcon, GearIcon, UploadSimpleIcon, TrashIcon, DeviceMobileIcon } from '@phosphor-icons/react';
import type { SceneOrigin, SupabaseSceneInstance, SupabaseSceneOrigin } from "@/types/sceneTypes";
import { convertToSceneOrigin, convertToSceneInstance, convertSceneOriginToStory } from "@/utils/dataConverters";
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import { storyPatchBody, storyToLegacy, type StoryDto } from '@/infrastructure/api/v1/mappers/stories';
import { sceneToLegacy, type SceneDto } from '@/infrastructure/api/v1/mappers/scenes';
import { projectPatchBody } from '@/infrastructure/api/v1/mappers/projects';
import { useAuthUser } from "@/hooks/useAuthUser";
import { useOrganization } from "@/hooks/useOrganization";
import { useServices } from "@/infrastructure/providers/ServiceProvider";
import { toast } from "sonner";
import projectPlaceholder from '@/assets/project-historical.jpg';
import { getSquiggleCropStyle } from '@/utils/squiggleCrop';
import { ensureProjectMapImage } from '@/utils/ensureProjectMapImage';
import { ProjectMapRef } from '@/components/project/project-map';
import { Story } from '@/types/cmsTypes';
import { usePlanState, requireReadyPlan } from '@/modules/subscriptionPlan/usePlanState';
import { splitDescription, joinDescription, reconcileStoryAttributions } from '@/modules/library/utils/storyAttributionUtils';
import { ImageButton } from '@/components/ui/image-button';
import { CardHeading } from '@/components/ui/card';
import { PageFrame } from '@/components/ui/page-frame';

// Legacy rows carry jsonb as JSON text, as the generic route sent it, whatever the Supabase row types say
const toStoryRow = (dto: StoryDto) => storyToLegacy(dto) as unknown as SupabaseSceneOrigin;
const toSceneRow = (dto: SceneDto) => sceneToLegacy(dto) as unknown as SupabaseSceneInstance;

/** The scene version as its legacy row, or null when it cannot be read. */
async function fetchSceneRow(sceneId: string) {
  const { data } = await toResult(v1.GET('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneId } } }));
  return data ? toSceneRow(data) : null;
}

const STORY_NOT_LOADED = "This story didn't load fully, so nothing was submitted. Reload the page and try again.";

/** Moves a scene version to a status through the server's lifecycle rules. */
const changeSceneStatus = (sceneId: string, status: SceneDto['status']) =>
  toResult(v1.POST('/api/v1/scenes/{sceneUniqueId}/status', { params: { path: { sceneUniqueId: sceneId } }, body: { status } }));

export default function StoryEdit() {
  const { projectId: routeProjectId, storyId } = useParams<{ projectId?: string; storyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthUser();
  const { subscriptionPlan, orgMember } = useOrganization();
  const planState = usePlanState();
  const services = useServices();
  const [showValidation, setShowValidation] = useState(false);

  if (!showValidation && new URLSearchParams(location.search).has('highlight')) {
    setShowValidation(true);
  }

  const {
    sceneOrigin,
    currentSceneInstance,
    project,
    loading,
    loadError,
    versions,
    loadingVersions,
    draftCount,
    latestDraftSceneInstanceId,
    hasImmersalScene,
    hasGeospatialAnchor,
    hasImageAnchorScene,
    uiImages,
    imageIds,
    setSceneOrigin,
    setSceneInstances,
    setCurrentSceneInstance,
    setUiImages,
    fetchUIImages,
    fetchVersionHistory,
    refetch,
    refreshInPlace
  } = useStoryEditData(storyId, user?.id);
  // `/story/edit/:storyId` carries no project id; the legacy `/projects/:projectId/stories/:storyId/edit` does.
  const projectId = routeProjectId ?? sceneOrigin?.projectInstanceId ?? project?.id;
  // A missing or inaccessible story may leave no project to return to.
  const projectPath = projectId ? `/project/${projectId}` : '/dashboard';

  const [saving, setSaving] = useState(false);
  const shareableSceneInstanceId = useMemo(
    () => versions.find((v) => v.status === 'live')?.id ?? versions.find((v) => v.status === 'pending')?.id ?? null,
    [versions],
  );
  // The story's permanent link rides on its live version (share links 3.6, 3.12)
  const liveVersionId = useMemo(() => versions.find((v) => v.status === 'live')?.id ?? null, [versions]);
  // A takedown revokes every link, so nothing here may mint, rotate or offer one
  const takenDown = sceneOrigin?.visibility === 'takenDown';
  const liveLink = useShareLink(takenDown ? null : liveVersionId, { expectedStatus: 'live' });
  const visibilitySwitch = useStoryVisibilitySwitch(sceneOrigin, setSceneOrigin);

  const publishVersionOptions = useMemo(
    () =>
      versions.map((v) =>
        makeVersionOption({
          sceneInstanceId: v.id,
          label: v.version,
          status: v.status,
          versionNumber: v.versionNumber,
        }),
      ),
    [versions],
  );

  const form = useStoryForm(sceneOrigin);
  const { title, description, latitude, longitude, address, city } = form.values;
  const { setTitle, setDescription, setAddress, setCity } = form;
  const locationSearch = useStoryLocationSearch(form);
  const guard = useUnsavedChangesGuard(form.dirty);
  const reconciledOriginIdRef = useRef<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const projectMapRef = useRef<ProjectMapRef>(null);

  const [imageUploadDialog, setImageUploadDialog] = useState<{
    open: boolean;
    title?: string;
    imageCategory?: 'SceneLandscape' | 'ScanImage' | 'ImageAnchor';
  }>({ open: false });

  const [publishingWarningDialog, setPublishingWarningDialog] = useState<{
    open: boolean;
    warningType: PublishingWarningType;
    onConfirm: () => void;
    liveLinkUrl?: string | null;
    killsLink?: boolean;
  }>({ open: false, warningType: 'archive-will-unpublish-project', onConfirm: () => {} });
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // Latest scene id for the share-dialog event, which fires only when the dialog opens
  const currentSceneId = currentSceneInstance?.sceneId;
  const shareSceneIdRef = useRef(currentSceneId);
  useLayoutEffect(() => {
    shareSceneIdRef.current = currentSceneId;
  });
  useEffect(() => {
    if (!shareDialogOpen) return;
    services.analytics.trackLureoShareDialogOpened({
      scene_instance_id: shareSceneIdRef.current,
      target: 'phone',
    });
  }, [shareDialogOpen, services.analytics]);
  const [publishFlowOpen, setPublishFlowOpen] = useState(false);
  const [publishFlowSceneId, setPublishFlowSceneId] = useState<string | null>(null);

  const [publishFlowDestination, setPublishFlowDestination] = useState<PublishDestination>('audience');
  const [templateJustSubmitted, setTemplateJustSubmitted] = useState(false);
  const isTemplateCopy = project?.projectCategory === CONSUMED_TEMPLATES_CATEGORY;
  const [saveToPublishOpen, setSaveToPublishOpen] = useState(false);
  const [saveToPublishSceneId, setSaveToPublishSceneId] = useState<string | null>(null);

  const openPublishFlow = (sceneInstanceId?: string | null, destination: PublishDestination = 'audience') => {
    if (isTemplateCopy) {
      setSaveToPublishSceneId(sceneInstanceId ?? latestDraftSceneInstanceId ?? currentSceneId ?? null);
      setSaveToPublishOpen(true);
      return;
    }
    setPublishFlowSceneId(sceneInstanceId ?? null);
    setPublishFlowDestination(destination);
    setTemplateJustSubmitted(false);
    setPublishFlowOpen(true);
  };

  const saveTemplateCopyToProject = async (target: { projectId: string } | { newProjectName?: string }) => {
    if (!saveToPublishSceneId) {
      toast.error('This story has no version to save');
      return;
    }
    if (hasUnsavedChanges() && !(await handleSave())) return;
    try {
      const copy = await copyScene(saveToPublishSceneId, target);
      toast.success('Scene saved to your project');
      if (sceneOrigin?.sceneOriginId) {
        try {
          await removeTemplateCopy(services, sceneOrigin.sceneOriginId);
        } catch (error) {
          console.error('Error removing the original template scene:', error);
          toast.error('Saved, but the original could not be removed from My Templates');
        }
      }
      navigate(publishCopyPath(copy));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save scene to a project');
    }
  };

  const publishRequested = new URLSearchParams(location.search).get('publish') === '1';
  const [publishRequestHandledFor, setPublishRequestHandledFor] = useState<string | null>(null);
  const storyLoaded = !loading && !loadingVersions && !!storyId && sceneOrigin?.sceneOriginId === storyId
    && !!project && project.id === sceneOrigin.projectInstanceId;
  if (publishRequested && storyLoaded && publishRequestHandledFor !== storyId) {
    setPublishRequestHandledFor(storyId);
    openPublishFlow(null);
  }
  useEffect(() => {
    if (!publishRequested || publishRequestHandledFor !== storyId) return;
    const params = new URLSearchParams(location.search);
    params.delete('publish');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [publishRequested, publishRequestHandledFor, storyId, location.pathname, location.search, navigate]);

  const [subscriptionLimitDialog, setSubscriptionLimitDialog] = useState<{
    open: boolean;
    limitType: 'cannot-publish' | 'publish-limit-reached' | 'no-version-history';
    currentLimit: number;
    currentCount: number;
  }>({ open: false, limitType: 'cannot-publish', currentLimit: 0, currentCount: 0 });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; title: string; type: 'project' | 'scene' } | null>(null);
  const [missingInfoDialogOpen, setMissingInfoDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  const validateGPSCoordinates = (): boolean => {
    if (!latitude || !longitude) return false;
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng)) return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    return true;
  };

  useLayoutEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);

  // The editor has no panel of its own, so a template submitted from there routes
  // here asking for it. Consumed once so a refresh doesn't reopen it.
  const templateDetailsRequested = new URLSearchParams(location.search).get('template') === 'details';
  const [templateSearch, setTemplateSearch] = useState<string | null>(null);
  if (templateSearch !== location.search) {
    setTemplateSearch(location.search);
    if (templateDetailsRequested) setTemplatePanelOpen(true);
  }
  useEffect(() => {
    if (!templateDetailsRequested) return;
    navigate(location.pathname, { replace: true });
  }, [templateDetailsRequested, location.search, location.pathname, navigate]);

  const sceneOriginId = sceneOrigin?.sceneOriginId;
  const templateStatus = useTemplateStatus(sceneOriginId, user?.id);
  const templateLineage = templateStatus.lineage;
  const templateSubmission = templateLineage?.submission ?? null;
  const refreshTemplateStatus = templateStatus.refresh;

  const currentStory: Story = {
    id: sceneOrigin?.sceneOriginId || 'temp',
    projectId: sceneOrigin?.projectInstanceId || projectId || '',
    title: title || 'Current Story',
    description: description || '',
    image: sceneOrigin?.sceneOriginCoverImgUrl || projectPlaceholder,
    order: 1,
    locationInfo: latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude)) ? {
      coordinates: {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude)
      },
      address: address || null
    } : null,
    status: 'draft' as const,
    createdAt: new Date(),
    updatedAt: new Date()
  };

  const getCurrentStoryStatus = (): 'draft' | 'live' | 'archived' | 'pending' | 'locked' | 'template' => {
    const lockedVersion = versions.find((version) => version.status === 'locked');
    if (lockedVersion) return 'locked';
    
    const liveVersion = versions.find((version) => version.status === 'live');
    if (liveVersion) return 'live';
    
    const pendingVersion = versions.find((version) => version.status === 'pending');
    if (pendingVersion) return 'pending';
    
    const templateVersion = versions.find((version) => version.status === 'template');
    if (templateVersion) return 'template';
    
    const draftVersion = versions.find((version) => version.status === 'draft');
    if (draftVersion) return 'draft';
    
    const archivedVersion = versions.find((version) => version.status === 'archived');
    if (archivedVersion) return 'archived';
    
    return 'draft';
  };

  const checkProjectHasOtherLiveScenes = async (sceneId: string): Promise<boolean> => {
    if (!projectId) return false;
    return await services.scene.checkProjectHasOtherLiveScenes(projectId, sceneId);
  };

  const checkUnpublishWillMakeProjectDraft = async (sceneId: string): Promise<boolean> => {
    if (!projectId) return false;
    return await services.scene.checkUnpublishWillMakeProjectDraft(projectId, sceneId);
  };

  useEffect(() => {
    const originId = sceneOrigin?.sceneOriginId;
    if (!originId || reconciledOriginIdRef.current === originId) return;
    reconciledOriginIdRef.current = originId;
    (async () => {
      const reconciled = await reconcileStoryAttributions(originId);
      if (reconciled === null) return;
      setSceneOrigin((prev) =>
        prev && prev.sceneOriginId === originId && (prev.sceneOriginDescription || '') !== reconciled
          ? { ...prev, sceneOriginDescription: reconciled }
          : prev
      );
    })();
  }, [sceneOrigin?.sceneOriginId, setSceneOrigin]);

  const hasUnsavedChanges = () => form.dirty;
  // The project's address only stands in for a story that has no place of its own
  const storyHasLocation = Boolean(address || (latitude && longitude));

  const versionIds = useMemo(() => new Set(versions.map((v) => v.id)), [versions]);
  useStoryChanges({
    storyId: sceneOriginId,
    versionIds,
    imageIds,
    refresh: () => void refreshInPlace(),
    refreshTemplateStatus: () => void refreshTemplateStatus(),
  });

  const imageWrites = useStoryImageWrites({
    sceneOriginId,
    projectId: project?.id,
    userId: user?.id,
    setUiImages,
    fetchUIImages,
    fetchVersionHistory,
  });
  const editCopyInFlight = useRef(false);

  if (loading) {
    return (
      <PageFrame layout="centred">
        <LoadingSpinner size="xl" />
      </PageFrame>
    );
  }

  if (loadError) {
    return (
      <PageFrame layout="centred">
        <Card padding="lg" className="text-center">
          <PageTitle as="h2" className="mb-4">Story didn't load</PageTitle>
          <p className="mb-6 text-lureo-ink-subtle">{loadError}</p>
          <div className="flex justify-center gap-2">
            <LureoButton variant="no-outline" onClick={() => navigate(projectPath)} className="h-9 px-4">
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </LureoButton>
            <LureoButton variant="secondary" onClick={() => void refetch()} className="h-9 rounded-lureo-radius-button-pill px-4">
              Try again
            </LureoButton>
          </div>
        </Card>
      </PageFrame>
    );
  }

  if (!sceneOrigin) {
    return (
      <PageFrame layout="centred">
        <Card padding="lg" className="text-center">
          <PageTitle as="h2" className="mb-4">Story Not Found</PageTitle>
          <p className="mb-6 text-lureo-ink-subtle">The story you're looking for doesn't exist.</p>
          <LureoButton variant="no-outline" onClick={() => navigate(projectPath)} className="h-9 w-9 p-0 md:w-auto md:px-4">
            <ArrowLeftIcon className="h-5 w-5" />
            <span className="hidden md:inline">{project?.projectName || (projectId ? 'Project' : 'Dashboard')}</span>
          </LureoButton>
        </Card>
      </PageFrame>
    );
  }

  const updateProjectMapScreenshot = async () => {
    if (!projectId || !user?.id) return;

    try {
      const sceneOriginsResponse = await listAll(page => v1.GET('/api/v1/stories', {
        params: { query: { projectUniqueId: [projectId], userUniqueId: user.id, sort: 'orderNumber', ...page } },
      }));

      if (sceneOriginsResponse.error) {
        throw sceneOriginsResponse.error;
      }

      const sceneOrigins = sceneOriginsResponse.data.map(toStoryRow);
      if (sceneOrigins.length === 0) return;

      const sceneOriginIds = sceneOrigins.map((so) => so.scene_origin_id);
      const { data: publishedInstances } = await listAllIn(sceneOriginIds, (chunk, page) => v1.GET('/api/v1/scenes', {
        params: { query: { storyUniqueId: chunk, status: ['live', 'pending'], ...page } },
      }));

      const publishedOriginIds = new Set((publishedInstances || []).map((i) => i.storyUniqueId));
      const filteredSceneOrigins = sceneOrigins.filter((so) => publishedOriginIds.has(so.scene_origin_id));
      const publishedStories = filteredSceneOrigins.map((sceneOrigin, index) =>
        convertSceneOriginToStory(convertToSceneOrigin(sceneOrigin), index + 1)
      );

      if (projectMapRef.current) {
        await projectMapRef.current.saveProjectMapScreenshot(publishedStories);
      }
    } catch (error) {
      console.error('Error updating project map screenshot:', error);
      throw error;
    }
  };

  const updateProjectLocationFromStory = async (storyLocationInfo: any) => {
    if (!projectId || !storyLocationInfo?.coordinates) return;

    try {
      const updateData = {
        project_instance_location_info: JSON.stringify({
          address: storyLocationInfo.address,
          city: storyLocationInfo.city,
          coordinates: {
            lat: storyLocationInfo.coordinates?.lat ?? 0,
            lng: storyLocationInfo.coordinates?.lng ?? 0
          }
        }),
      };

      const response = await toResult(v1.PATCH('/api/v1/projects/{projectUniqueId}', {
        params: { path: { projectUniqueId: projectId } },
        body: projectPatchBody(updateData),
      }));

      if (response.error) {
        console.error('Error updating project location from story:', response.error);
        throw response.error;
      }

    } catch (error) {
      console.error('Failed to update project location from story:', error);
      throw error;
    }
  };

  // Resolves true once the server took the save; a failure toasts and resolves false, so callers stay put
  const handleSave = async (): Promise<boolean> => {
    if (!sceneOrigin || !user?.id) return false;
    const origin = sceneOrigin;
    const moveProject = form.locationChanged;

    try {
      setSaving(true);

      const locationInfo =
        latitude && longitude
          ? {
              address: address || null,
              city: city || null,
              coordinates: {
                lat: parseFloat(latitude),
                lng: parseFloat(longitude)
              }
            }
          : null;

      // Preserve the attribution block that may have been injected by the editor
      const { attributions } = splitDescription(origin.sceneOriginDescription || '');
      const fullDescription = joinDescription(description, attributions);

      // Only the fields the user changed, so a save never writes back a value changed elsewhere since the page loaded
      const updateData = {
        ...(form.titleChanged ? { scene_origin_name: title } : {}),
        ...(form.descriptionChanged ? { scene_origin_description: fullDescription } : {}),
        ...(moveProject ? { scene_origin_location_info: locationInfo ? JSON.stringify(locationInfo) : null } : {}),
      };

      const response = await toResult(v1.PATCH('/api/v1/stories/{storyUniqueId}', {
        params: { path: { storyUniqueId: origin.sceneOriginId } },
        body: storyPatchBody(updateData),
      }));

      if (response.error || !response.data) {
        console.error('Error updating scene origin:', response.error || 'No rows updated');
        toast.error("Failed to save story");
        return false;
      }

      setSceneOrigin((prev) => prev && prev.sceneOriginId === origin.sceneOriginId
        ? {
            ...prev,
            ...(form.titleChanged ? { sceneOriginName: title } : {}),
            ...(form.descriptionChanged ? { sceneOriginDescription: fullDescription } : {}),
            ...(moveProject ? { sceneOriginLocationInfo: locationInfo } : {}),
          }
        : prev);

      const reconciled = await reconcileStoryAttributions(origin.sceneOriginId);
      if (reconciled !== null && reconciled !== fullDescription) {
        setSceneOrigin((prev) => prev && prev.sceneOriginId === origin.sceneOriginId ? { ...prev, sceneOriginDescription: reconciled } : prev);
      }

      fetchVersionHistory(origin.sceneOriginId);

      // The project follows a story's location only when the story moved
      if (moveProject && locationInfo && projectId) {
        try {
          await updateProjectLocationFromStory(locationInfo);
          await updateProjectMapScreenshot();
          toast.success('Story and project location updated!');
        } catch (error) {
          console.error('Failed to update project location or map screenshot:', error);
          toast.success("Story saved. The project's location couldn't be updated.");
        }
      } else {
        toast.success('Story saved successfully');
      }
      return true;
    } catch (error) {
      console.error('Error saving story:', error);
      toast.error('Failed to save story');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleOpenInEditor = (sceneInstanceId?: string) => {
    if (sceneInstanceId) {
      navigate(`/editor/${sceneInstanceId}`);
      return;
    }

    const draftVersions = versions.filter(v => v.status === 'draft' || v.status === 'template');

    if (draftVersions.length > 0) {
      const latestDraftVersion = draftVersions[0];
      navigate(`/editor/${latestDraftVersion.id}`);
    }
  };

  const copySource = pickPublishedVersion(versions) ?? pickNewestDraft(versions) ?? pickNewestVersion(versions);

  const handleMakeEditableCopy = async (version: StoryVersion) => {
    if (editCopyInFlight.current) return;
    const userPlan = requireReadyPlan(planState);
    if (!userPlan) return;
    const hasVersionHistory = services.subscriptionPlan.hasVersionHistory(userPlan);
    
    if (!hasVersionHistory) {
      setSubscriptionLimitDialog({
        open: true,
        limitType: 'no-version-history',
        currentLimit: 0,
        currentCount: 0
      });
      return;
    }

    editCopyInFlight.current = true;
    try {
      const { sceneInstanceId: newSceneInstanceId, versionNumber: newVersionNumber } = await services.scene.makeEditableCopy(
        user.id,
        version.id,
        projectId
      );

      toast.success(`Draft Version ${newVersionNumber} created successfully`);

      await fetchVersionHistory(sceneOrigin.sceneOriginId);
      navigate(`/editor/${newSceneInstanceId}`);
    } catch (error) {
      console.error('Error creating draft copy:', error);
      toast.error('Failed to create draft copy');
    } finally {
      editCopyInFlight.current = false;
    }
  };

  const handleUnpublishVersion = async (version: StoryVersion) => {
    if (!user?.id) {
      toast.error('Unable to unpublish version: missing user information');
      return;
    }

    try {
      const willMakeProjectDraft = await checkUnpublishWillMakeProjectDraft(version.id);
      // Taking the live version away kills the public link, so warn first, also while the link is unread
      const killsLink = version.status === 'live' && (Boolean(liveLink.url) || liveLink.linkUnknown);

      if (willMakeProjectDraft || killsLink) {
        setPublishingWarningDialog({
          open: true,
          warningType: willMakeProjectDraft ? 'unpublish-will-make-project-draft' : 'unpublish-will-kill-link',
          liveLinkUrl: killsLink ? liveLink.url : null,
          killsLink,
          onConfirm: async () => {
            await performUnpublishVersion(version);
          }
        });
      } else {
        await performUnpublishVersion(version);
      }
    } catch (error) {
      console.error('Error unpublishing version:', error);
      toast.error('Failed to unpublish version');
    }
  };

  const performUnpublishVersion = async (version: StoryVersion) => {
    try {
      await services.scene.unpublishVersion(
        user.id,
        sceneOrigin.sceneOriginId,
        version.versionNumber,
        projectId
      );

      toast.success(`Version ${version.versionNumber} unpublished successfully`);
      invalidateShareLinks(sceneOrigin.sceneOriginId);

      await fetchVersionHistory(sceneOrigin.sceneOriginId);

      if (projectId && user?.id) {
        try {
          await ensureProjectMapImage(services, projectId, user.id);
        } catch (mapError) {
          console.error('Update project map image after unpublish failed', mapError);
        }
      }
    } catch (error) {
      console.error('Error performing unpublish version:', error);
      toast.error('Failed to unpublish version');
    }
  };

  const handleArchiveDraftVersion = async (version: StoryVersion) => {
    if (!user?.id) {
      toast.error('Unable to archive draft version: missing user information');
      return;
    }

    const userPlan = requireReadyPlan(planState);
    if (!userPlan) return;
    const hasVersionHistory = services.subscriptionPlan.hasVersionHistory(userPlan);
    if (!hasVersionHistory) {
      setSubscriptionLimitDialog({
        open: true,
        limitType: 'no-version-history',
        currentLimit: 0,
        currentCount: 0
      });
      return;
    }

    try {
      await services.scene.archiveDraftVersion(user.id, version.id);

      toast.success(`Version ${version.versionNumber} archived`);

      await fetchVersionHistory(sceneOrigin.sceneOriginId);
    } catch (error) {
      console.error('Error archiving draft version:', error);
      toast.error('Failed to archive draft version');
    }
  };

  const handleArchiveVersion = async (version: StoryVersion) => {
    const userPlan = requireReadyPlan(planState);
    if (!userPlan) return;
    const hasVersionHistory = services.subscriptionPlan.hasVersionHistory(userPlan);
    
    if (!hasVersionHistory) {
      setSubscriptionLimitDialog({
        open: true,
        limitType: 'no-version-history',
        currentLimit: 0,
        currentCount: 0
      });
      return;
    }

    const unpublishesProject = version.status === 'live' && !(await checkProjectHasOtherLiveScenes(version.id));
    const killsLink = version.status === 'live' && (Boolean(liveLink.url) || liveLink.linkUnknown);

    if (unpublishesProject || killsLink) {
      setPublishingWarningDialog({
        open: true,
        warningType: unpublishesProject ? 'archive-will-unpublish-project' : 'archive-will-kill-link',
        liveLinkUrl: killsLink ? liveLink.url : null,
        killsLink,
        onConfirm: async () => {
          await performArchiveVersion(version);
        }
      });
    } else {
      await performArchiveVersion(version);
    }
  };

  const performArchiveVersion = async (version: StoryVersion) => {
    const result = await services.scene.handleSceneArchive(version.id);
    if (result.success) {
      toast.success(`Version ${version.version} archived`);
      invalidateShareLinks(sceneOrigin.sceneOriginId);
      await fetchVersionHistory(sceneOrigin.sceneOriginId);
      if (projectId) {
        try {
          await services.project.updateProjectPublishedStatus(projectId);
        } catch (updateError) {
          console.error('Error updating project published status after archiving:', updateError);
        }
        if (user?.id) {
          try {
            await ensureProjectMapImage(services, projectId, user.id);
          } catch (mapError) {
            console.error('Update project map image after archive failed', mapError);
          }
        }
      }
    } else {
      toast.error(result.message);
    }
  };

  // Each field is written once: the modal's value where its step showed, else an unsaved edit on the page
  const publishMetadataFrom = (args: PublishFlowSubmitArgs): StoryPublishMetadata => {
    const modalLocation = args.address !== undefined || args.latitude !== undefined || args.longitude !== undefined;
    const location = modalLocation
      ? { address: args.address, latitude: args.latitude, longitude: args.longitude, city: args.city }
      : form.locationChanged
        ? { address, latitude, longitude, city }
        : {};
    return {
      storyName: args.storyName ?? (form.titleChanged ? title : undefined),
      sceneIntroduction: args.sceneIntroduction ?? (form.descriptionChanged ? description : undefined),
      ...location,
    };
  };

  // Puts what the publish flow just wrote into the page, which the account stream does not echo back to this tab
  const adoptPublishMetadata = (meta: StoryPublishMetadata, visibility?: SceneOrigin['visibility']) => {
    if (!sceneOrigin) return;
    const next = { ...withStoryMetadata(sceneOrigin, meta), ...(visibility ? { visibility } : {}) };
    form.adopt(next);
    setSceneOrigin(next);
  };

  const handlePublishFlowSubmit = async (args: PublishFlowSubmitArgs) => {
    const targetSceneId = args.sceneInstanceId ?? currentSceneInstance?.sceneId;
    if (!targetSceneId) throw new Error('No scene to publish');

    if (args.destination === 'template') {
      await handleTemplateFlowSubmit(targetSceneId, args);
      return;
    }

    if (!project || !sceneOrigin) throw new Error(STORY_NOT_LOADED);
    const meta = publishMetadataFrom(args);

    let submitted;
    try {
      submitted = await submitStoryForReview(services, {
        storyId: sceneOrigin.sceneOriginId,
        sceneId: targetSceneId,
        devices: args.devices,
        visibility: args.visibility,
        name: meta.storyName,
        introduction: meta.sceneIntroduction,
        address: meta.address,
        latitude: meta.latitude,
        longitude: meta.longitude,
        city: meta.city,
        listing: {
          creatorName: args.creatorName,
          projectName: args.projectName,
          projectDescription: args.projectDescription,
          projectTags: args.projectTags,
        },
      }, project.id);
    } catch (error) {
      if (!(error instanceof PublishIncompleteError)) throw error;
      setValidationErrors(toValidationErrors(error.errors, { [sceneOrigin.sceneOriginId]: meta.storyName ?? sceneOrigin.sceneOriginName }));
      setMissingInfoDialogOpen(true);
      throw new PublishValidationError();
    }

    invalidateShareLinks(sceneOrigin.sceneOriginId);
    adoptPublishMetadata(meta, args.visibility);
    toast.success(`Story version: 'v${submitted.versionNumber}' has been submitted for review. We'll let you know within 7 days.`);
    try {
      await fetchVersionHistory(sceneOrigin.sceneOriginId);
      if (projectId && user?.id) await ensureProjectMapImage(services, projectId, user.id);
    } catch (followUpError) {
      console.error('Refresh after submitting for review failed', followUpError);
    } finally {
      void refreshInPlace();
    }
  };

  // Template destination. The story itself is never touched: the chosen version
  // is copied and only the copy becomes the template.
  const handleTemplateFlowSubmit = async (targetSceneId: string, args: PublishFlowSubmitArgs) => {
    if (!sceneOrigin || !user?.id) throw new Error('No story to submit');
    const meta = publishMetadataFrom(args);

    // storyName here is the story's own field from step 1, not the template's. The
    // template name travels separately as args.templateName and only ever lands on
    // the copy, so a template called "Reusable Garry" leaves the story as "Garry".
    await writePublishFlowMetadata({
      sceneOriginId: sceneOrigin.sceneOriginId,
      projectInstanceId: project?.id,
      userId: user.id,
      creatorName: args.creatorName,
      ...meta,
    });
    adoptPublishMetadata(meta);

    // The gallery modal shows the template's address, so back-fill it from the
    // coordinates before the copy inherits scene_origin_location_info.
    await ensureTemplateLocationPersisted();

    await submitStoryAsTemplate(services, {
      sceneInstanceId: targetSceneId,
      storyName: args.templateName || args.storyName || sceneOrigin.sceneOriginName,
      description: args.templateDescription ?? '',
      learnings: args.templateLearnings ?? '',
      category: args.templateCategory ?? '',
      difficulty: args.templateDifficulty ?? '',
      tags: args.templateTags ?? [],
      features: args.templateFeatures ?? [],
    });

    setTemplateJustSubmitted(true);

    await refreshTemplateStatus();
  };

  const handleImageUpload = () => {
    setImageUploadDialog({
      open: true,
      title: 'Upload Header Image',
      imageCategory: 'SceneLandscape'
    });
  };

  const locationImageLabel = hasGeospatialAnchor ? 'Location Image' : 'Scan Image';

  const handleLocationImageUpload = () => {
    setImageUploadDialog({
      open: true,
      title: `Upload ${locationImageLabel}`,
      imageCategory: 'ScanImage'
    });
  };

  const handleAnchorImageUpload = () => {
    setImageUploadDialog({
      open: true,
      title: 'Upload Anchor Image',
      imageCategory: 'ImageAnchor'
    });
  };

  const ensureTemplateLocationPersisted = async () => {
    if (!sceneOrigin?.sceneOriginId || !user?.id) return;
    const storyIdForLocation = sceneOrigin.sceneOriginId;
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (address) return;

    const geo = await reverseGeocode(lat, lng);
    if (!geo?.address) return;

    const locationInfo = {
      ...(geo.city ? { city: geo.city } : {}),
      address: geo.address,
      coordinates: { lat, lng }
    };
    const { error } = await toResult(v1.PATCH('/api/v1/stories/{storyUniqueId}', {
      params: { path: { storyUniqueId: storyIdForLocation } },
      body: storyPatchBody({ scene_origin_location_info: JSON.stringify(locationInfo) }),
    }));
    if (error) {
      console.error('Template location back-fill failed', error);
      return;
    }
    setAddress(geo.address);
    if (geo.city) setCity(geo.city);
    setSceneOrigin((prev) => prev && prev.sceneOriginId === storyIdForLocation ? { ...prev, sceneOriginLocationInfo: locationInfo } : prev);
  };

  // The guard asks first when the form holds unsaved edits
  const handleBackClick = () => navigate(projectPath);

  const handleSaveAndExit = async () => {
    if (saving) return;
    if (await handleSave()) guard.proceed();
    else guard.cancel();
  };

  const handleDiscardAndExit = () => guard.proceed();

  const copyToClipboard = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      toast.error(`Could not copy the ${what.toLowerCase()}`);
    }
  };

  // A draft's link is minted on first use; the live version carries the story's permanent link
  const linkForVersion = async (version: StoryVersion): Promise<string | null> => {
    if (version.status === 'live') return liveLink.url;
    if (isPreviewStatus(version.status)) return shareLinkUrl((await services.scene.mintDraftShareLink(version.id)).slug);
    return null;
  };

  const handleCopyLink = async (version: StoryVersion) => {
    try {
      const url = await linkForVersion(version);
      if (!url) {
        toast.error('This version has no link');
        return;
      }
      await copyToClipboard(url, 'Link');
      services.analytics.trackLureoSceneLinkCopied({
        scene_instance_id: version.id,
        link_type: version.status === 'live' ? 'share' : 'draft_share',
      });
    } catch (error) {
      console.error('Copy link failed', error);
      toast.error('Could not get the link');
    }
  };

  // Only a preview link swaps: a live link is revoked and created again instead
  const handleSwapLink = (version: StoryVersion) => {
    if (!isPreviewStatus(version.status)) return;
    setPublishingWarningDialog({
      open: true,
      warningType: 'swap-link',
      onConfirm: async () => {
        try {
          await services.scene.rotateShareLink((await services.scene.mintDraftShareLink(version.id)).slug);
          clearShareLinkCache(version.id);
          toast.success('Link swapped. The old link no longer works; the Spectacles code is unchanged.');
        } catch (error) {
          console.error('Swap link failed', error);
          toast.error('Could not swap the link');
        }
      },
    });
  };

  const handleCopyLiveLink = () => {
    const live = versions.find((v) => v.status === 'live');
    if (live) handleCopyLink(live);
  };

  const handleRevokeLiveLink = () => {
    if (!liveLink.url) return;
    setPublishingWarningDialog({
      open: true,
      warningType: 'revoke-link',
      liveLinkUrl: liveLink.url,
      onConfirm: async () => {
        try {
          await liveLink.revoke();
          toast.success('Public link revoked');
        } catch (error) {
          console.error('Revoke link failed', error);
          toast.error('Could not revoke the link');
        }
      },
    });
  };

  const handleCreateLiveLink = async () => {
    try {
      const link = await liveLink.createLive();
      if (link) toast.success('Public link created');
    } catch (error) {
      console.error('Create link failed', error);
      toast.error('Could not create the link');
    }
  };

  const handleDeleteStory = () => {
    if (!sceneOrigin) return;
    
    setItemToDelete({
      id: sceneOrigin.sceneOriginId,
      title: sceneOrigin.sceneOriginName || 'Untitled Story',
      type: 'scene'
    });
    setDeleteDialogOpen(true);
  };

  // Admin function to change scene instance status
  const handleAdminStatusChange = async (newStatus: 'draft' | 'live' | 'pending' | 'archived' | 'template', sceneInstanceId?: string) => {
    const targetSceneInstanceId = sceneInstanceId || currentSceneInstance?.sceneId;
    
    if (!targetSceneInstanceId) {
      toast.error('No scene instance selected');
      return;
    }

    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    try {
      if (newStatus === 'template') {
        const sceneInstanceData = await fetchSceneRow(targetSceneInstanceId);

        if (!sceneInstanceData) {
          throw new Error('Failed to fetch scene instance');
        }

        // One server move: the story's other template versions return to draft, and a scene type is created when the scene has none
        const { error: updateError } = await changeSceneStatus(targetSceneInstanceId, newStatus);

        if (updateError) throw updateError;
        toast.success(
          sceneInstanceData.scene_type_id
            ? `Status changed to ${newStatus}`
            : `Status changed to ${newStatus} and scene type created`
        );
        services.analytics.trackLureoTemplateCreated({ template_scene_instance_id: targetSceneInstanceId });
      } else {
        const { error } = await changeSceneStatus(targetSceneInstanceId, newStatus);

        if (error) {
          throw error;
        }

        toast.success(`Status changed to ${newStatus}`);
      }
      invalidateShareLinks(sceneOrigin?.sceneOriginId);
      
      // Update the local status state if this is the current scene instance
      // Refresh the scene instances to reflect the change
      // A story id is required: without one the list would widen to every readable scene
      const { data: updatedSceneInstances, error: fetchError } = sceneOrigin?.sceneOriginId
        ? await listAll(page => v1.GET('/api/v1/scenes', {
            params: { query: { storyUniqueId: [sceneOrigin.sceneOriginId], sort: '-versionNumber', ...page } },
          }))
        : { data: null, error: null };

      if (fetchError) {
        console.error('Error fetching updated scene instances:', fetchError);
      } else if (updatedSceneInstances) {
        const convertedInstances = updatedSceneInstances.map(dto => convertToSceneInstance(toSceneRow(dto)));
        setSceneInstances(convertedInstances);
        
        // Update current scene instance if it's the one that was changed
        const updatedInstance = convertedInstances.find(inst => inst.sceneId === targetSceneInstanceId);
        if (updatedInstance && targetSceneInstanceId === currentSceneInstance?.sceneId) {
          setCurrentSceneInstance(updatedInstance);
        }
        
        // Refresh version history to show updated status
        if (sceneOrigin?.sceneOriginId) {
          await fetchVersionHistory(sceneOrigin.sceneOriginId);
        }
      }
    } catch (error) {
      console.error('Error updating scene instance status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleConfirmDelete = async (item: { id: string; title: string; type: 'project' | 'scene' }) => {
    if (!user?.id) return;

    try {
      // The server renumbers the remaining stories and refreshes the project summary in the same transaction
      await services.scene.deleteSceneOriginRow(item.id);

      // An unknown plan unlocks nothing
      const features = planState.plan ? services.subscriptionPlan.getFeatureLimits(planState.plan) : null;

      if (features && features.maxStories !== -1) {
        try {
          // Fetch all remaining scene_origins for this user
          const { data: storyDtos } = await listAll(page => v1.GET('/api/v1/stories', { params: { query: { userUniqueId: user.id, ...page } } }));
          const allSceneOrigins = storyDtos?.map(toStoryRow);

          if (allSceneOrigins && allSceneOrigins.length > 0) {
            // Single query: fetch all scene instances for user's origins (avoids N+1)
            const originIds = allSceneOrigins.map((o) => o.scene_origin_id);
            const { data: sceneDtos } = await listAllIn(originIds, (chunk, page) => v1.GET('/api/v1/scenes', {
              params: { query: { storyUniqueId: chunk, ...page } },
            }));
            const allInstances = sceneDtos?.map(toSceneRow);

            // Group instances by scene_origin_id and compute status per story
            const instancesByOrigin = new Map<string, typeof allInstances>();
            for (const inst of allInstances || []) {
              const list = instancesByOrigin.get(inst.scene_origin_id) || [];
              list.push(inst);
              instancesByOrigin.set(inst.scene_origin_id, list);
            }

            const storiesWithStatus = allSceneOrigins.map((origin) => {
              const sceneInstances = instancesByOrigin.get(origin.scene_origin_id) || [];
              let actualStatus: 'draft' | 'live' | 'archived' | 'pending' | 'locked' | 'template' = 'draft';
              if (sceneInstances.some((i) => i.scene_instance_status === 'locked')) actualStatus = 'locked';
              else if (sceneInstances.some((i) => i.scene_instance_status === 'live')) actualStatus = 'live';
              else if (sceneInstances.some((i) => i.scene_instance_status === 'pending')) actualStatus = 'pending';
              else if (sceneInstances.some((i) => i.scene_instance_status === 'draft')) actualStatus = 'draft';
              else if (sceneInstances.some((i) => i.scene_instance_status === 'template')) actualStatus = 'template';
              else if (sceneInstances.some((i) => i.scene_instance_status === 'archived')) actualStatus = 'archived';
              return {
                id: origin.scene_origin_id,
                name: origin.scene_origin_name || 'Untitled',
                lastUpdated: new Date(origin.updated_at || Date.now()),
                status: actualStatus
              };
            });
            
            // Count current editable stories (non-locked, non-archived)
            const editableStories = storiesWithStatus.filter(
              s => s.status !== 'archived' && s.status !== 'locked' && s.status !== 'template'
            );
            
            // If we're now below the limit, unlock the most recently updated locked story
            if (editableStories.length < features.maxStories) {
              const lockedStories = storiesWithStatus
                .filter(s => s.status === 'locked')
                .sort((a, b) => b.lastUpdated.getTime() - a.lastUpdated.getTime());
              
              if (lockedStories.length > 0) {
                const storyToUnlock = lockedStories[0];
                
                // Return the story's locked versions to draft
                const { error: unlockError } = await toResult(v1.POST('/api/v1/stories/{storyUniqueId}/unlock', {
                  params: { path: { storyUniqueId: storyToUnlock.id } },
                }));

                if (!unlockError) {
                  toast.success(`Story "${storyToUnlock.name}" is now editable again`);
                }
              }
            }
          }
        } catch (unlockError) {
          console.error('Error checking/unlocking stories:', unlockError);
          // Don't fail the deletion if unlock check fails
        }
      }
      
      toast.success('Story deleted successfully');
      guard.leave(() => navigate(projectPath));
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Failed to delete story');
    }
  };

  return (
    <PageFrame layout="content" className="select-none">
      {/* Header */}
      <div className="sticky top-0 z-lureo-layer-bar surface-bar">
        <div className="container py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 md:gap-4">
              <LureoButton onClick={handleBackClick} variant="no-outline" className="h-9 w-9 shrink-0 p-0 md:w-auto md:px-4">
                <ArrowLeftIcon className="h-5 w-5" />
                <span className="hidden md:inline">{project?.projectName || 'Project'}</span>
              </LureoButton>
              <div className="hidden h-6 w-px shrink-0 bg-lureo-line md:block" />
              <PageTitle className="truncate">Edit - {sceneOrigin?.sceneOriginName || 'Story'}</PageTitle>
              <div className="shrink-0">
                <StatusBadge status={getCurrentStoryStatus()} />
              </div>
            </div>

            {/* Desktop buttons - hidden when template is accepted and locked */}
            <div className="hidden items-center space-x-2 md:flex">
              <LureoButton
                onClick={handleSave}
                variant="outline-2"
                disabled={saving || !hasUnsavedChanges()}
                className="h-9 rounded-lureo-radius-button-pill px-3">
                <FloppyDiskIcon className="h-5 w-5" />
                {saving ? 'Saving...' : 'Save'}
              </LureoButton>
              <LureoButton
                onClick={() => setShareDialogOpen(true)}
                variant="outline-2"
                className="h-9 rounded-lureo-radius-button-pill px-3"
                tooltipContent="Test this story on your phone, no need to publish"
                tooltipPosition="bottom">
                <DeviceMobileIcon className="h-5 w-5" />
                Test
              </LureoButton>
              {draftCount > 0 && (
                <LureoButton variant="outline-2" onClick={() => handleOpenInEditor(latestDraftSceneInstanceId)} className="h-9 rounded-lureo-radius-button-pill px-4">
                  Open Story
                  <ArrowRightIcon className="h-5 w-5" />
                </LureoButton>
              )}
              <LureoButton
                onClick={() => openPublishFlow(null)}
                variant="secondary"
                className="h-9 rounded-lureo-radius-button-pill px-3">
                <UploadSimpleIcon className="h-5 w-5" />
                Publish
              </LureoButton>
            </div>

            {/* Mobile menu */}
            <div className="md:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <LureoButton variant="outline-2" aria-label="Menu" className="h-9 w-9 rounded-full p-0">
                    <GearIcon className="h-4 w-4" />
                  </LureoButton>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <div className="space-y-4 pt-6">
                    {draftCount > 0 && (
                      <>
                        <LureoButton
                          onClick={() => {
                            handleOpenInEditor(latestDraftSceneInstanceId);
                          }}
                          variant="secondary"
                          disabled={isMobileDevice()}
                          className="h-12 w-full rounded-lureo-radius-button-pill px-3">
                          Open Story in Editor
                          <ArrowRightIcon className="h-5 w-5" />
                        </LureoButton>
                        {isMobileDevice() && <DesktopOnlyHint className="block text-center" />}
                      </>
                    )}
                    <LureoButton
                      onClick={() => {
                        handleSave();
                      }}
                      variant="outline-2"
                      className="h-12 w-full rounded-lureo-radius-button-pill"
                      disabled={saving || !hasUnsavedChanges()}>
                      <FloppyDiskIcon className="h-5 w-5" />
                      {saving ? 'Saving...' : 'Save'}
                    </LureoButton>
                    <LureoButton
                      onClick={() => setShareDialogOpen(true)}
                      variant="outline-2"
                      className="h-12 w-full rounded-lureo-radius-button-pill">
                      <DeviceMobileIcon className="h-5 w-5" />
                      Test
                    </LureoButton>
                    <LureoButton
                      onClick={() => openPublishFlow(null)}
                      variant="secondary"
                      className="h-12 w-full rounded-lureo-radius-button-pill">
                      <UploadSimpleIcon className="h-5 w-5" />
                      Publish
                    </LureoButton>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8">
        {takenDown && (
          <div className="mb-6">
            <TakedownBanner origin={{ suppressed_at: sceneOrigin?.suppressedAt, suppressed_reason: sceneOrigin?.suppressedReason }} />
          </div>
        )}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Story Settings */}
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeading title="Details" />

              <div className="space-y-6">
                <div>
                  <FieldLabel variant="plain" mb htmlFor="story-title">Title</FieldLabel>
                  <Input
                    id="story-title"
                    className={`select-text ${getFieldErrorClass(!validateField('scene_name', title), showValidation && !!sceneOrigin)}`}
                    value={title || ''}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter story title..."
                  />
                </div>

                <div>
                  <FieldLabel variant="plain" mb htmlFor="story-introduction">Introduction</FieldLabel>
                  <Textarea
                    id="story-introduction"
                    className={`select-text ${getFieldErrorClass(!validateField('scene_description', description), showValidation && !!sceneOrigin)}`}
                    value={description || ''}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what happens at this location..."
                    rows={3}
                  />
                  {/* Attribution block: auto-managed, read-only */}
                  {(() => {
                    const { attributions } = splitDescription(sceneOrigin?.sceneOriginDescription || '');
                    if (attributions.length === 0) return null;
                    return (
                      <div className="mt-3">
                        <span className="mb-2 block text-lureo-14 font-medium text-lureo-ink-strong leading-5">Attributions</span>
                        <ul className="space-y-1">
                          {attributions.map((attr, i) => (
                            <li key={i} className="text-lureo-12 text-lureo-ink-subtle leading-relaxed">
                              {attr}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Card>

            <StoryDestinationsCard
              versions={versions}
              templateSubmission={templateSubmission}
              templateUsageSummary={templateStatus.usage}
              templateUsageFailed={templateStatus.usageFailed}
              onRetryTemplateStatus={templateStatus.lineageFailed ? () => void refreshTemplateStatus() : undefined}
              onUpdateSubmission={() => openPublishFlow(null, 'template')}
              onViewTemplate={templateLineage?.templateSceneOriginId ? () => setTemplatePanelOpen(true) : undefined}
              shareLink={
                liveLink.url
                  ? {
                      url: liveLink.url,
                      onCopy: handleCopyLiveLink,
                      onRevoke: handleRevokeLiveLink,
                    }
                  : null
              }
              onCreateShareLink={liveLink.canCreate ? handleCreateLiveLink : undefined}
              onRetryShareLink={liveLink.error ? liveLink.refetch : undefined}
              visibility={sceneOrigin?.visibility}
              onVisibilityChange={visibilitySwitch.onChange}
              visibilitySaving={visibilitySwitch.saving}
              onEditCopy={copySource ? () => handleMakeEditableCopy(copySource) : undefined}
            />

            <StoryLocationCard
              projectMapRef={projectMapRef}
              currentStory={currentStory}
              latitude={latitude}
              longitude={longitude}
              address={address}
              city={city}
              onAddressChange={locationSearch.onAddressChange}
              onMapPick={locationSearch.onMapPick}
              onPlacePick={(lat, lng, placeAddress) => locationSearch.fillPlace(lat, lng, placeAddress)}
              searchQuery={locationSearch.searchQuery}
              onSearchQueryChange={locationSearch.onSearchQueryChange}
              onSearchKeyPress={locationSearch.onSearch}
              onAutocompleteSelect={locationSearch.onAutocompleteSelect}
              onCoordinateChange={locationSearch.onCoordinateChange}
              autocompleteResults={locationSearch.autocompleteResults}
              showAutocomplete={locationSearch.showAutocomplete}
              onAutocompleteClose={locationSearch.closeAutocomplete}
              isSearching={locationSearch.isSearching}
              isGoogleMapsLoaded={locationSearch.isGoogleMapsLoaded}
              projectId={projectId}
              userId={user?.id}
              validateGPSCoordinates={validateGPSCoordinates}
              showValidation={showValidation}
              hasSceneOrigin={!!sceneOrigin}
              isLocationRequired={currentSceneInstance?.sceneInstanceAnchorCategory === 'geospatial_anchor'}
              getFieldErrorClass={getFieldErrorClass}
              searchInputRef={searchInputRef}
            />
          </div>

          {/* Sidebar */}
          <div className="flex flex-col justify-between space-y-6">
            <div className="space-y-6">
              {shareableSceneInstanceId && !takenDown && (
                <StoryShareCard
                  sceneInstanceId={shareableSceneInstanceId}
                  anchorCategory={currentSceneInstance?.sceneInstanceAnchorCategory}
                />
              )}

              <Card>
                <CardHeading icon={<UploadSimpleIcon />} title="Cover Image" />

                <ImageButton label="Change cover image" className="h-48 w-full rounded-lureo-radius-lg" invalid={!validateField('scene_cover_image', uiImages.coverImageUrl) && showValidation && !!sceneOrigin} onClick={() => handleImageUpload()}>
                    {!uiImages.coverImageUrl ? (
                    // Use squiggle image with different crop areas when no cover image
                    <span
                      className="block h-full w-full"
                      style={getSquiggleCropStyle(storyId || '', { scaleRange: [2.75, 3.25], size: 'quarter' })}
                    />
                  ) : (
                    <SceneCoverImage
                      imageUrl={uiImages.coverImageUrl}
                      crop={uiImages.coverImageCrop}
                      size="full"
                      className="h-full w-full"
                      showCrop={!!uiImages.coverImageCrop}
                    />
                  )}
                </ImageButton>
              </Card>

              {(hasImmersalScene || hasGeospatialAnchor) && (
                <Card>
                  <CardHeading icon={<UploadSimpleIcon />} title={locationImageLabel} />
                  <ImageButton round label={`Change ${locationImageLabel.toLowerCase()}`} className="mx-auto h-48 w-48" invalid={!validateField('scene_scan_image', uiImages.scanImageUrl) && showValidation && !!sceneOrigin} onClick={() => handleLocationImageUpload()}>
                    {uiImages.scanImageUrl ? (
                      <ScanImage
                        imageUrl={uiImages.scanImageUrl}
                        crop={uiImages.scanImageCrop}
                        size="full"
                        className="h-full w-full"
                        showCrop={!!uiImages.scanImageCrop}
                      />
                    ) : (
                      <span className="block h-full w-full bg-linear-to-t from-lureo-aqua/90 from-0% via-lureo-surface via-70% to-lureo-surface" />
                    )}
                  </ImageButton>
                </Card>
              )}

              {hasImageAnchorScene && (
                <Card>
                  <CardHeading icon={<UploadSimpleIcon />} title="Anchor Image" />
                  <ImageButton label="Change anchor image" className="h-48 w-full rounded-lureo-radius-lg" invalid={!validateField('scene_anchor_image', uiImages.anchorImageUrl) && showValidation && !!sceneOrigin} onClick={() => handleAnchorImageUpload()}>
                    {uiImages.anchorImageUrl ? (
                      <SceneCoverImage
                        imageUrl={uiImages.anchorImageUrl}
                        crop={uiImages.anchorImageCrop}
                        size="full"
                        className="h-full w-full"
                        showCrop={!!uiImages.anchorImageCrop}
                      />
                    ) : (
                      <span className="block h-full w-full bg-linear-to-t from-lureo-aqua/90 from-0% via-lureo-surface via-70% to-lureo-surface" />
                    )}
                  </ImageButton>
                </Card>
              )}

              <StoryVersionHistory
                versions={versions}
                loadingVersions={loadingVersions}
                subscriptionPlan={subscriptionPlan}
                subscriptionPlanService={services.subscriptionPlan}
                onUnpublishVersion={handleUnpublishVersion}
                onArchiveVersion={handleArchiveVersion}
                onMakeEditableCopy={handleMakeEditableCopy}
                onPublishDraft={(sceneInstanceId) => openPublishFlow(sceneInstanceId)}
                onAdminStatusChange={handleAdminStatusChange}
                onArchiveDraftVersion={handleArchiveDraftVersion}
                onOpenInEditor={handleOpenInEditor}
                onCopyLink={takenDown ? undefined : handleCopyLink}
                onSwapLink={takenDown ? undefined : handleSwapLink}
              />
            </div>

            {/* Danger Zone */}
            <Card>
              <CardTitle as="h3" className="mb-4 flex items-center">
                <TrashIcon className="mr-2 h-5 w-5 text-lureo-danger-ink" />
                Danger Zone
              </CardTitle>
              <p className="mb-4 text-lureo-14 text-lureo-ink-subtle leading-5">
                Permanently delete this story and all its versions. This action cannot be undone.
              </p>
              <LureoButton
                variant="danger"
                className="w-full h-9 min-h-[44px] rounded-lureo-radius-button-pill sm:min-h-0"
                onClick={handleDeleteStory}>
                Delete Story
              </LureoButton>
            </Card>
          </div>
        </div>
      </div>

      {/* Image Upload Dialog */}
      <UIImageUploadDialog
        open={imageUploadDialog.open}
        onOpenChange={(open) => setImageUploadDialog({ ...imageUploadDialog, open })}
        onImageSelect={(imageUrl) => void imageWrites.onImageSelect(imageUploadDialog.imageCategory || 'SceneLandscape', imageUrl)}
        onCropApplied={(data) => void imageWrites.onCropApplied(imageUploadDialog.imageCategory || 'SceneLandscape', data)}
        title={imageUploadDialog.title}
        description={imageUploadDialog.imageCategory === 'ScanImage' 
          ? "Upload a scan image for your story. It will be displayed as a circle to match the mobile app UI."
          : imageUploadDialog.imageCategory === 'ImageAnchor'
          ? "Upload an anchor image for your story. It should match the image used as an image anchor in your story."
          : "Upload a header image for your story or generate one using AI."
        }
        currentImage={imageUploadDialog.imageCategory === 'ScanImage' 
          ? uiImages.scanImageUrl 
          : imageUploadDialog.imageCategory === 'ImageAnchor'
          ? uiImages.anchorImageUrl
          : uiImages.coverImageUrl
        }
        currentOriginalImage={imageUploadDialog.imageCategory === 'ScanImage' 
          ? uiImages.scanImageOriginalUrl 
          : imageUploadDialog.imageCategory === 'ImageAnchor'
          ? uiImages.anchorImageOriginalUrl
          : uiImages.coverImageOriginalUrl
        }
        imageCategory={imageUploadDialog.imageCategory || 'SceneLandscape'}
        sceneOriginId={sceneOrigin?.sceneOriginId}
        enableCrop={imageUploadDialog.imageCategory === 'SceneLandscape' || imageUploadDialog.imageCategory === 'ScanImage' || imageUploadDialog.imageCategory === 'ImageAnchor'}
        closeOnRemove={true}
      />

      {/* Exit Confirmation Dialog */}
      <ExitConfirmationDialog
        open={guard.asking}
        onOpenChange={(open) => {
          if (!open && !saving) guard.cancel();
        }}
        onSaveAndExit={handleSaveAndExit}
        onDiscardAndExit={handleDiscardAndExit}
        saving={saving}
      />

      <TemplateDetailsPanel
        open={templatePanelOpen}
        onOpenChange={setTemplatePanelOpen}
        templateSceneOriginId={templateLineage?.templateSceneOriginId ?? null}
        submission={templateSubmission}
        usageSummary={templateStatus.usage}
      />

      <PublishFlowModal
        open={publishFlowOpen}
        onOpenChange={setPublishFlowOpen}
        storyName={title}
        creatorName={
          orgMember?.fullName ||
          (user?.user_metadata?.full_name as string | undefined) ||
          (user?.user_metadata?.name as string | undefined) ||
          project?.sourceName ||
          user?.email ||
          ''
        }
        anchorCategory={currentSceneInstance?.sceneInstanceAnchorCategory}
        projectInstanceId={project?.id ?? null}
        sceneOriginId={sceneOrigin?.sceneOriginId ?? null}
        sceneInstanceId={publishFlowSceneId ?? currentSceneInstance?.sceneId ?? null}
        sceneCoverImgUrl={currentSceneInstance?.sceneCoverImgUrl ?? null}
        versions={publishVersionOptions}
        initialDestination={publishFlowDestination}
        onViewTemplate={
          templateJustSubmitted
            ? () => {
                setPublishFlowOpen(false);
                setTemplatePanelOpen(true);
              }
            : undefined
        }
        initialProjectName={project?.projectName || ''}
        initialProjectDescription={project?.description || ''}
        initialProjectTags={project?.tags || []}
        initialAddress={address || (storyHasLocation ? '' : project?.locationInfo?.address || '')}
        initialLatitude={latitude}
        initialLongitude={longitude}
        initialCity={city || (storyHasLocation ? '' : [project?.locationInfo?.city].find((c) => c && c !== 'null') || '')}
        initialSceneIntroduction={description}
        onSubmit={handlePublishFlowSubmit}
      />

      {/* "How do I test in the mobile app?" instructions */}
      <EditorShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        sceneInstanceId={currentSceneInstance?.sceneId}
      />

      {/* Publishing Warning Dialog */}
      <PublishingWarningDialog
        open={publishingWarningDialog.open}
        onOpenChange={(open) => setPublishingWarningDialog({ ...publishingWarningDialog, open })}
        onConfirm={publishingWarningDialog.onConfirm}
        onCancel={() => {}}
        warningType={publishingWarningDialog.warningType}
        projectName="this project" // TODO: Fetch actual project name if needed
        storyName={sceneOrigin?.sceneOriginName}
        hasUnsavedChanges={hasUnsavedChanges()}
        liveLinkUrl={publishingWarningDialog.liveLinkUrl}
        killsLink={publishingWarningDialog.killsLink}
      />

      {/* Subscription Limit Dialog */}
      <SubscriptionLimitDialog
        open={subscriptionLimitDialog.open}
        onOpenChange={(open) => setSubscriptionLimitDialog({ ...subscriptionLimitDialog, open })}
        limitType={subscriptionLimitDialog.limitType}
        currentLimit={subscriptionLimitDialog.currentLimit}
        userPlan={planState.plan ?? ''}
      />

      {/* Missing Info Dialog */}
      <MissingInfoDialog
        open={missingInfoDialogOpen}
        onOpenChange={setMissingInfoDialogOpen}
        onCancel={() => {
          setMissingInfoDialogOpen(false);
          // Trigger validation mode when user cancels from missing info dialog
          setShowValidation(true);
        }}
        errors={validationErrors}
        projectName={project?.projectName || 'this project'}
        projectId={projectId}
        sceneOriginId={sceneOrigin?.sceneOriginId || storyId}
      />

      <TemplateExitDialog
        open={saveToPublishOpen}
        onOpenChange={setSaveToPublishOpen}
        mode="publish"
        onSaveAsNewProject={(newProjectName) => void saveTemplateCopyToProject({ newProjectName })}
        onAddToExistingProject={(targetProjectId) => void saveTemplateCopyToProject({ projectId: targetProjectId })}
      />

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        item={itemToDelete}
        liveLinkUrl={liveLink.url}
      />
    </PageFrame>
  );
}
